import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Navigate } from "react-router-dom";
import { Save, Sliders, Check, ScanFace, Search, ShieldAlert } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Switch } from "components/ui/switch";
import ThresholdBar from "components/ThresholdBar";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

// Admin → Global (Super Admin only): deployment-wide liveness policy defaults PLUS every
// tunable biometric parameter. Biometric fields are optional — blank = inherit the built-in
// default (shown as the input placeholder). Each field carries a detailed help note.
export default function GlobalConfig() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // The recognition model is read-only by default: changing it invalidates every enrollment,
  // so it goes through an explicit dialog with a typed confirmation (re-checked server side).
  const [modelOpen, setModelOpen] = useState(false);
  const [modelWanted, setModelWanted] = useState("buffalo_l");
  const [modelTyped, setModelTyped] = useState("");
  const [modelErr, setModelErr] = useState("");
  const [modelBusy, setModelBusy] = useState(false);

  const applyModel = async () => {
    setModelBusy(true); setModelErr("");
    try {
      const res = await api.put("/api/global-config/", {
        biometric_recognizer: modelWanted,
        recognizer_confirm: modelTyped.trim(),
      });
      setForm(res.data);
      setModelOpen(false);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setModelErr(e?.response?.data?.detail || t("globalConfig.modelFailed", "Não foi possível alterar o modelo."));
    } finally { setModelBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/api/global-config/");
      setForm(res.data);
    } catch {
      setError(t("globalConfig.loadError", "Não foi possível carregar a configuração global."));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  if (user && !user.is_admin) return <Navigate to="/liveness/admin/client-apps" replace />;

  // "" (blank) → null so the backend inherits the default; numbers pass through.
  const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError(""); setSaved(false);
    try {
      const res = await api.put("/api/global-config/", {
        ai_threshold: Number(form.ai_threshold) || 0,
        require_geo: !!form.require_geo,
        require_gesture: !!form.require_gesture,
        session_ttl_seconds: Number(form.session_ttl_seconds) || 1800,
        // biometric tunables (null = inherit default)
        biometric_auth_similarity: numOrNull(form.biometric_auth_similarity),
        biometric_kyc_similarity: numOrNull(form.biometric_kyc_similarity),
        biometric_same_person_min: numOrNull(form.biometric_same_person_min),
        biometric_match_similarity: numOrNull(form.biometric_match_similarity),
        biometric_dup_similarity: numOrNull(form.biometric_dup_similarity),
        // biometric_recognizer is intentionally NOT sent here — it only changes through the
        // dedicated confirmation dialog (which the backend re-verifies).
        ann_min_enroll: numOrNull(form.ann_min_enroll),
        ann_nprobe: numOrNull(form.ann_nprobe),
        ann_retrain_growth: numOrNull(form.ann_retrain_growth),
        ann_retrain_min_delta: numOrNull(form.ann_retrain_min_delta),
        flashmark_block_cut: numOrNull(form.flashmark_block_cut),
        oidc_confidence_margin: numOrNull(form.oidc_confidence_margin),
        oidc_separation_margin: numOrNull(form.oidc_separation_margin),
        duplicate_similarity: numOrNull(form.duplicate_similarity),
        document_face_similarity: numOrNull(form.document_face_similarity),
        document_face_deny: numOrNull(form.document_face_deny),
      });
      setForm(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err?.response?.data?.detail
        || JSON.stringify(err?.response?.data || {})
        || t("globalConfig.saveError", "Falha ao salvar."));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>;
  }

  const field = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40";

  // A tunable number field: label, value, default-as-placeholder, and a detailed help note.
  const Num = ({ k, label, help, def, step = "0.01", min, max }) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input className={field} type="number" step={step} min={min} max={max}
        placeholder={`${t("globalConfig.defaultIs", "padrão")}: ${def}`}
        value={form[k] ?? ""}
        onChange={(e) => set(k, e.target.value)} />
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{help}</p>
    </div>
  );

  return (
    <div className="animate-fade-in w-full">
      <div className="flex items-center gap-2 mb-1">
        <Sliders className="h-5 w-5 text-primary" />
        <h1 className="text-2xl font-bold tracking-tight">{t("globalConfig.title", "Configuração global")}</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {t("globalConfig.subtitle", "Padrões globais da política de liveness (nível mais baixo de precedência).")}
        {" "}<code>/api/v1/liveness/start</code>{" "}
        <strong>{t("globalConfig.precedence", "cliente > empresa (ClientApp) > global")}</strong>.
      </p>

      <form onSubmit={save} className="w-full space-y-6">
        {/* ── Liveness ─────────────────────────────────────────── */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-2">
              <Sliders className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold tracking-tight">{t("globalConfig.secLiveness", "Vivacidade (liveness)")}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t("globalConfig.aiThreshold", "Limiar de IA (%)")}</label>
                <input className={field} type="number" min={0} max={100}
                  value={form.ai_threshold} onChange={(e) => set("ai_threshold", e.target.value)} />
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {t("globalConfig.aiThresholdHelp", "Confiança mínima (%) da IA de vivacidade/gesto para aprovar, quando nem o cliente nem o ClientApp definem. Maior = mais rígido (mais reprovações), menor = mais permissivo.")}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t("globalConfig.sessionTtl", "TTL da sessão (segundos)")}</label>
                <input className={field} type="number" min={60} max={86400}
                  value={form.session_ttl_seconds} onChange={(e) => set("session_ttl_seconds", e.target.value)} />
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {t("globalConfig.sessionTtlHelp", "Por quanto tempo um link de verificação continua válido após o /start. Depois disso a sessão expira e é preciso gerar outra.")}
                </p>
              </div>
              <Num k="flashmark_block_cut" def="0.50" min="0" max="1"
                label={t("globalConfig.flashCut", "Gate do Flashmark")}
                help={t("globalConfig.flashCutHelp", "Correlação mínima (0–1) entre os flashes coloridos da tela e o reflexo no rosto para considerar VIVO. Abaixo disso a sessão é reprovada como spoof (foto/tela). Maior = mais rígido contra telas, porém mais sensível a ambientes muito claros.")} />
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Switch checked={!!form.require_geo} onChange={(v) => set("require_geo", v)}
                ariaLabel={t("globalConfig.requireGeoAria", "Exigir geolocalização")} />
              <span>{t("globalConfig.requireGeo", "Exigir geolocalização por padrão")}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Switch checked={!!form.require_gesture} onChange={(v) => set("require_gesture", v)}
                ariaLabel={t("globalConfig.requireGestureAria", "Exigir gesto")} />
              <span>{t("globalConfig.requireGesture", "Exigir desafio de gesto por padrão")}</span>
            </div>
          </CardContent>
        </Card>

        {/* ── Face matching thresholds ─────────────────────────── */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-2">
              <ScanFace className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold tracking-tight">{t("globalConfig.secMatch", "Reconhecimento facial (limiares de match)")}</h2>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              {t("globalConfig.cosineNote", "Valores de similaridade de cosseno (0 a 1). Maior = mais rígido (menos falsos positivos, porém mais rejeições de pessoas legítimas). Deixe em branco para usar o padrão.")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Num k="biometric_auth_similarity" def="0.50" min="0" max="1"
                label={t("globalConfig.authSim", "Login facial (1:1 e 1:N)")}
                help={t("globalConfig.authSimHelp", "Corte para autenticar por biometria — tanto o login facial próprio (1:N entre usuários do sistema) quanto a autenticação 1:1 de aplicativos. Este é o parâmetro mais sensível de segurança: subir reduz o risco de aceitar a pessoa errada (FAR), descer facilita o acesso.")} />
              <Num k="biometric_kyc_similarity" def="0.40" min="0" max="1"
                label={t("globalConfig.kycSim", "KYC — selfie × documento")}
                help={t("globalConfig.kycSimHelp", "Corte para considerar que a selfie ao vivo é a mesma pessoa da foto do documento. Costuma ser MENOR que o login, pois comparar uma foto impressa/antiga com um rosto ao vivo é mais difícil (impressão, idade, iluminação).")} />
              <Num k="biometric_same_person_min" def="0.35" min="0" max="1"
                label={t("globalConfig.samePerson", "Gate 'mesma pessoa' (captura)")}
                help={t("globalConfig.samePersonHelp", "Durante a captura, exige que o rosto recortado, o quadro completo e a foto do gesto sejam a MESMA pessoa. Abaixo deste valor a sessão é reprovada (troca de rosto / pessoas diferentes no vídeo). Subir demais pode reprovar capturas legítimas com ângulos variados.")} />
              <Num k="biometric_match_similarity" def="0.45" min="0" max="1"
                label={t("globalConfig.matchSim", "Vínculo automático (registro de pessoas)")}
                help={t("globalConfig.matchSimHelp", "No registro de Pessoas, corte para vincular automaticamente uma nova captura a uma pessoa já existente na mesma empresa. Maior = cria mais pessoas separadas; menor = agrupa mais (risco de juntar pessoas diferentes).")} />
              <Num k="biometric_dup_similarity" def="0.95" min="0" max="1"
                label={t("globalConfig.dupSim", "Foto duplicada")}
                help={t("globalConfig.dupSimHelp", "Acima deste valor duas fotos são tratadas como a MESMA imagem (não apenas a mesma pessoa) e a duplicata não é armazenada. Bem alto de propósito; baixar pode descartar fotos diferentes válidas da mesma pessoa.")} />
              <Num k="duplicate_similarity" def="0.80" min="0" max="1"
                label={t("globalConfig.dupAlert", "Alerta de biometria duplicada")}
                help={t("globalConfig.dupAlertHelp", "Acima deste valor duas biometrias CADASTRADAS de pessoas diferentes geram um alerta ao Super Admin (possível mesma pessoa em dois registros). Maior = menos alertas (só sósias quase idênticos).")} />
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-1">{t("globalConfig.docFace", "Documento × rosto (Ouro)")}</label>
                <p className="text-xs text-muted-foreground mb-3">
                  {t("globalConfig.docBandsHelp", "Faixas de decisão da similaridade entre a FOTO do documento e a biometria cadastrada: abaixo do 1º limiar o documento é recusado; entre os dois vai para revisão manual de um admin; acima do 2º concede o nível Ouro automaticamente.")}
                </p>
                <ThresholdBar
                  deny={Number(form.document_face_deny ?? 0.35)}
                  allow={Number(form.document_face_similarity ?? 0.5)}
                  onChange={(d, a) => setForm((f) => ({ ...f, document_face_deny: d, document_face_similarity: a }))}
                  labels={{ deny: t("globalConfig.bandDeny", "Recusar"), review: t("globalConfig.bandReview", "Revisar"), allow: t("globalConfig.bandAllow", "Permitir") }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t("globalConfig.recognizer", "Modelo de reconhecimento (ArcFace)")}</label>
                <div className="flex items-center gap-3">
                  <div className={`${field} flex items-center bg-muted/40 text-muted-foreground cursor-default`}>
                    {(form.biometric_recognizer || "") || `${t("globalConfig.recognizerDefault", "padrão")} (buffalo_l)`}
                  </div>
                  <Button type="button" variant="outline" onClick={() => { setModelWanted(form.biometric_recognizer || "buffalo_l"); setModelTyped(""); setModelErr(""); setModelOpen(true); }}>
                    {t("globalConfig.change", "Alterar")}
                  </Button>
                </div>
                <p className="text-xs text-amber-500 mt-1 leading-relaxed inline-flex gap-1">
                  <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  {t("globalConfig.recognizerHelp", "Modelo que gera a assinatura facial. TROCAR INVALIDA todos os cadastros existentes (o espaço vetorial muda) — será preciso recadastrar todo mundo e re-treinar o pré-filtro. Só altere com um plano de migração.")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── ANN pre-filter ───────────────────────────────────── */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold tracking-tight">{t("globalConfig.secAnn", "Pré-filtro de busca 1:N (ANN)")}</h2>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              {t("globalConfig.annNote", "Acelera a busca 1:N filtrando a base por um índice grosseiro antes de comparar (e descriptografar) registro a registro. Não afeta a decisão de match, só o desempenho e a cobertura da busca.")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Num k="ann_nprobe" def="32" step="1" min="1" max="4096"
                label={t("globalConfig.nprobe", "Centróides consultados (nprobe)")}
                help={t("globalConfig.nprobeHelp", "Quantos 'baldes' do índice a busca abre. Maior = mais candidatos comparados (recall maior, porém mais descriptografias e mais lento); menor = mais rápido, com risco de pular o verdadeiro match.")} />
              <Num k="ann_min_enroll" def="500" step="1" min="1"
                label={t("globalConfig.annMin", "Mínimo de cadastros para treinar")}
                help={t("globalConfig.annMinHelp", "Abaixo desta quantidade o pré-filtro NÃO é treinado e a busca faz varredura completa (seguro para recall). Só faz sentido treinar o índice quando a base é grande o bastante.")} />
              <Num k="ann_retrain_growth" def="0.25" step="0.05" min="0" max="10"
                label={t("globalConfig.retrainGrowth", "Crescimento para re-treinar")}
                help={t("globalConfig.retrainGrowthHelp", "Fração de crescimento da base (ex.: 0.25 = +25%) desde o último treino para o re-treino automático (no reaper) disparar. Maior = re-treina com menos frequência.")} />
              <Num k="ann_retrain_min_delta" def="200" step="1" min="1"
                label={t("globalConfig.retrainDelta", "Novos cadastros para re-treinar")}
                help={t("globalConfig.retrainDeltaHelp", "Além do percentual, exige pelo menos esta quantidade de novos templates desde o último treino antes de re-treinar. Evita re-treinos caros por variações pequenas.")} />
            </div>
          </CardContent>
        </Card>

        {/* ── OIDC face-login e-mail gate ──────────────────────── */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-2">
              <ScanFace className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold tracking-tight">{t("globalConfig.secOidc", "Login facial OIDC — confirmação por e-mail")}</h2>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              {t("globalConfig.oidcNote", "No login facial de apps externos (OIDC), quando o match 1:N fica AMBÍGUO exigimos uma confirmação por e-mail (código). Estes dois valores definem o que conta como 'ambíguo'.")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Num k="oidc_confidence_margin" def="0.08" min="0" max="1"
                label={t("globalConfig.oidcConf", "Margem de confiança")}
                help={t("globalConfig.oidcConfHelp", "Quanto o melhor match precisa superar o limiar de autenticação para ser aceito SEM confirmação por e-mail. Maior = exige e-mail com mais frequência (mais seguro, menos cômodo).")} />
              <Num k="oidc_separation_margin" def="0.06" min="0" max="1"
                label={t("globalConfig.oidcSep", "Separação 1º–2º")}
                help={t("globalConfig.oidcSepHelp", "Distância mínima entre o 1º e o 2º rostos mais parecidos. Se dois candidatos ficam próximos demais, é ambíguo → pede e-mail. Maior = mais rígido contra sósias.")} />
            </div>
          </CardContent>
        </Card>

        {error && <p className="text-sm text-red-500">{error}</p>}
        <div className="flex items-center gap-3">
          <Button type="submit" loading={saving}>
            <Save className="h-4 w-4 mr-2" /> {t("common.save", "Salvar")}
          </Button>
          {saved && (
            <span className="text-sm text-emerald-500 inline-flex items-center gap-1">
              <Check className="h-4 w-4" /> {t("common.saved", "Salvo")}
            </span>
          )}
        </div>
      </form>

      {modelOpen && createPortal(
        <div className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl bg-card border border-red-500/40 shadow-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="h-5 w-5 text-red-500" />
              <h2 className="font-semibold">{t("globalConfig.modelTitle", "Alterar o modelo de reconhecimento")}</h2>
            </div>

            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm space-y-1 mb-4">
              <p className="text-red-400 font-medium">{t("globalConfig.modelWarn", "Esta alteração invalida TODAS as biometrias cadastradas.")}</p>
              <p className="text-muted-foreground text-xs leading-relaxed">
                {t("globalConfig.modelWarnBody", "O modelo gera a assinatura facial: trocá-lo muda o espaço vetorial, então nenhum template existente pode mais ser comparado. Todas as pessoas precisarão se recadastrar, o pré-filtro (ANN) terá de ser re-treinado e as autenticações falharão até lá.")}
              </p>
            </div>

            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm mb-4">
              <dt className="text-muted-foreground">{t("globalConfig.modelCurrent", "Modelo atual")}</dt>
              <dd className="font-mono">{form.recognizer_impact?.current || "buffalo_l"}</dd>
              <dt className="text-muted-foreground">{t("globalConfig.modelNew", "Novo modelo")}</dt>
              <dd>
                <select className="h-9 w-full rounded-lg border border-border bg-background text-sm px-2"
                  value={modelWanted} onChange={(e) => { setModelWanted(e.target.value); setModelTyped(""); }}>
                  <option value="buffalo_l">buffalo_l — {t("globalConfig.recognizerL", "mais preciso (autenticação)")}</option>
                  <option value="buffalo_sc">buffalo_sc — {t("globalConfig.recognizerSc", "mais leve/rápido")}</option>
                </select>
              </dd>
              <dt className="text-muted-foreground">{t("globalConfig.modelEnrollments", "Cadastros invalidados")}</dt>
              <dd className="font-mono text-red-400">{form.recognizer_impact?.enrollments ?? "—"}</dd>
              <dt className="text-muted-foreground">{t("globalConfig.modelPeople", "Pessoas afetadas")}</dt>
              <dd className="font-mono text-red-400">{form.recognizer_impact?.people ?? "—"}</dd>
              <dt className="text-muted-foreground">{t("globalConfig.modelGold", "Biometrias Ouro")}</dt>
              <dd className="font-mono text-red-400">{form.recognizer_impact?.gold ?? "—"}</dd>
            </dl>

            <p className="text-xs text-muted-foreground mb-1.5">
              {t("globalConfig.modelType", "Para confirmar, digite")} <code className="px-1 py-0.5 rounded bg-muted text-foreground font-mono select-all">{modelWanted}</code>
            </p>
            <input value={modelTyped} onChange={(e) => setModelTyped(e.target.value)} autoFocus
              autoComplete="off" spellCheck={false} placeholder={modelWanted}
              className={`w-full h-10 rounded-lg border bg-background text-sm px-3 font-mono ${modelTyped && modelTyped.trim() !== modelWanted ? "border-red-500/60" : "border-border"}`} />
            {modelErr && <p className="text-sm text-red-500 mt-2">{modelErr}</p>}

            <div className="flex justify-end gap-2 mt-5">
              <Button type="button" size="sm" variant="outline" onClick={() => setModelOpen(false)}>
                {t("common.cancel", "Cancelar")}
              </Button>
              <Button type="button" size="sm" loading={modelBusy}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={modelTyped.trim() !== modelWanted || modelWanted === (form.recognizer_impact?.current || "buffalo_l")}
                onClick={applyModel}>
                {t("globalConfig.modelApply", "Alterar modelo")}
              </Button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}
